import React from "react";
import { t } from "@auto/services";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import EChart from "@src/reporting/components/EChart";
import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import { reportingResourceKey } from "@src/reporting/model/reportingFilters.mjs";
import {
  count,
  dateTime,
  operatorDisplayName,
} from "@src/reporting/model/reportingFormatters.mjs";
import { loadReportingOperators } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const OperatorActivityWidget = ({ filter, refreshToken = 0 }) => {
  const resource = useReportingResource(
    () => loadReportingOperators(filter),
    {
      key: reportingResourceKey("operators", filter),
      refreshToken,
      initialData: [],
    },
  );

  const rows = resource.data || [];
  const chartRows = React.useMemo(() => rows.slice(0, 15).reverse(), [rows]);
  const option = React.useMemo(() => ({
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { left: 160, right: 48, top: 18, bottom: 30 },
    xAxis: { type: "value", minInterval: 1 },
    yAxis: {
      type: "category",
      data: chartRows.map(operatorDisplayName),
      axisLabel: { width: 148, overflow: "truncate" },
    },
    series: [
      {
        name: t("reporting.applicants"),
        type: "bar",
        data: chartRows.map((row) => row.applicationCount),
      },
    ],
  }), [chartRows]);

  return (
    <ReportingWidget
      title={t("reporting.operatorPerformance")}
      subtitle={t("reporting.operatorPerformanceSubtitle")}
      loading={resource.loading}
      error={resource.error}
      empty={resource.hasLoaded && rows.length === 0}
    >
      <div className={styles.chartTableGrid}>
        <EChart option={option} loading={resource.loading} className={styles.operatorChart} />
        <div className={styles.tableWrap}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("voter.operator")}</TableCell>
                  <TableCell align="right">{t("reporting.applicants")}</TableCell>
                  <TableCell>{t("reporting.lastActivity")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, 25).map((row) => (
                  <TableRow key={row.nationalId}>
                    <TableCell>
                      <div className={styles.primaryCell}>{operatorDisplayName(row)}</div>
                      <div className={styles.secondaryCell}>
                        NINU {row.nationalId || "—"}
                        {row.accountCount ? ` · ${count(row.accountCount)} ${Number(row.accountCount) === 1 ? t("common.accountLower") : t("common.accountsLower")}` : ""}
                      </div>
                      {row.username ? <div className={styles.secondaryCell}>{row.username}</div> : null}
                    </TableCell>
                    <TableCell align="right">{count(row.applicationCount)}</TableCell>
                    <TableCell>{dateTime(row.lastSubmissionAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </ReportingWidget>
  );
};

export default OperatorActivityWidget;
